import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { AuthService } from 'src/app/authentication/auth.service';
import { Usuario } from 'src/app/authentication/interfaces/interfaces';
import { ClinicalOrder } from 'src/app/interfaces/gestion-clinica/medical-record.interface';
import { MedicalRecordsService } from 'src/app/services/gestion-clinica/medical-records.service';
import { OrdenesService } from 'src/app/services/transacciones/ordenes.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-historia-clinica',
  templateUrl: './historia-clinica.component.html',
  styleUrls: ['./historia-clinica.component.scss'],
  standalone: false
})
export class HistoriaClinicaComponent implements OnInit, OnDestroy {
  rows: ClinicalOrder[] = [];
  loading = false;
  total = 0;
  limit = 10;
  offset = 0;
  searchTerm = '';
  errorMessage = '';
  currentYear = new Date().getFullYear();
  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();
  private usuario: Usuario;

  constructor(
    private medicalRecordsService: MedicalRecordsService,
    private authService: AuthService,
    private router: Router,
    private ordenesService: OrdenesService,
    private toastr: ToastrService
  ) {
    this.usuario = this.authService.loginUsuario;
  }

  ngOnInit(): void {
    this.searchSubject.pipe(
      debounceTime(350),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe((term) => {
      this.searchTerm = term;
      this.offset = 0;
      this.load();
    });
    this.load();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  load(): void {
    this.loading = true;
    this.medicalRecordsService.pendingOrders({
      search: this.searchTerm,
      year: this.currentYear,
      limit: this.limit,
      offset: this.offset,
      perfil: this.usuario.perfil || '',
      sucursal: this.usuario.sucursal || ''
    }).subscribe({
      next: (resp: any) => {
        this.loading = false;
        this.errorMessage = '';
        this.rows = resp?.rows || [];
        this.total = resp?.total || 0;
      },
      error: (err: any) => {
        this.loading = false;
        this.errorMessage = err?.error?.message || err?.message
          || 'No se pudo consultar la información. Revise la búsqueda e intente nuevamente.';
      }
    });
  }

  buscar(): void {
    this.offset = 0;
    this.load();
  }

  buscarMientrasEscribe(value: string): void {
    this.searchSubject.next(value);
  }

  limpiar(): void {
    this.searchTerm = '';
    this.offset = 0;
    this.load();
  }

  cambiarLimite(value: string): void {
    this.limit = Number(value) || 10;
    this.offset = 0;
    this.load();
  }

  paginaAnterior(): void {
    if (this.offset <= 0) return;
    this.offset = Math.max(0, this.offset - this.limit);
    this.load();
  }

  paginaSiguiente(): void {
    if (this.offset + this.limit >= this.total) return;
    this.offset = this.offset + this.limit;
    this.load();
  }

  primeraPagina(): void {
    this.offset = 0;
    this.load();
  }

  ultimaPagina(): void {
    const paginas = Math.max(Math.ceil(this.total / this.limit), 1);
    this.offset = (paginas - 1) * this.limit;
    this.load();
  }

  get desde(): number {
    return this.total === 0 ? 0 : this.offset + 1;
  }

  get hasta(): number {
    return Math.min(this.offset + this.limit, this.total);
  }

  get paginaActual(): number {
    return Math.floor(this.offset / this.limit) + 1;
  }

  verOrden(row: ClinicalOrder): void {
    const pdfWindow = window.open('', '_blank');
    this.ordenesService.generarOrdenTransferencia(
      localStorage.getItem('token'),
      row.order_number || '',
      row.doctor_name || '',
      row.doctor_phone || ''
    ).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        if (pdfWindow) {
          pdfWindow.location.href = url;
        } else {
          window.open(url, '_blank');
        }
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      },
      error: () => {
        pdfWindow?.close();
        this.toastr.error('No se pudo generar la orden digital.', 'Atención');
      }
    });
  }

  crearHistoria(row: ClinicalOrder): void {
    this.router.navigate(['/sistema/gestion-clinica/historia-clinica/crear', row.order_id]);
  }
}
